// FILE: components/sections/ForDevelopersSection.tsx

import React from 'react';
import { SectionContainer, SubSection } from '../Content';
import { Breadcrumb } from '../Breadcrumb';
import CodeBlock from '../CodeBlock';

const componentUsageCode = `import React from 'react';
import { Button, Icon } from '@acutrack-bookprint/acutrack-ds';

function MyComponent() {
    return (
        <Button 
            variant="primary" 
            size="lg" 
            leftIcon={<Icon>check</Icon>}
            onClick={() => alert('Clicked!')}
        >
            Confirm
        </Button>
    );
}`;

const stylingCode = `<div className="bg-bg-primary p-4 rounded-lg border border-border-color">
  <h3 className="font-heading text-text-primary text-2xl">A Card Title</h3>
  <p className="font-body text-text-secondary mt-2">
    This component's styles are entirely defined by utility classes.
  </p>
</div>
`;

export const ForDevelopersSection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({ groupTitle, sectionTitle, id }) => (
    <SectionContainer id={id}>
        <Breadcrumb group={groupTitle} section={sectionTitle} />
        <h2 className="section-title">Developer Guide</h2>
        <p className="section-subtitle">This guide provides developers with a complete overview of the Acutrack Design System's architecture, technology stack, and the core philosophies behind its construction. Understanding these principles is key to using and contributing to the system effectively.</p>
        
        <SubSection title="Installation & Setup">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                Get started with the Acutrack Design System in your project. Follow these steps to install and configure the system.
            </p>
            
            <div className="space-y-6">
                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">1. Install the Package</h4>
                    <p className="text-text-secondary dark:text-dark-text-secondary mb-3">
                        Install the design system package using npm or yarn:
                    </p>
                    <CodeBlock code={`# Using npm
npm install @acutrack-bookprint/acutrack-ds

# Using yarn
yarn add @acutrack-bookprint/acutrack-ds

# Using pnpm
pnpm add @acutrack-bookprint/acutrack-ds`} />
                </div>

                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">2. Install Peer Dependencies</h4>
                    <p className="text-text-secondary dark:text-dark-text-secondary mb-3">
                        The design system requires React 18.3.1+ or React 19.0.0+ and React DOM:
                    </p>
                    <CodeBlock code={`# If you don't have React installed
npm install react react-dom

# For TypeScript support
npm install -D @types/react @types/react-dom`} />
                </div>

                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">3. Install Tailwind CSS</h4>
                    <p className="text-text-secondary dark:text-dark-text-secondary mb-3">
                        The design system is built on Tailwind CSS. Install and configure it:
                    </p>
                    <CodeBlock code={`# Install Tailwind CSS
npm install -D tailwindcss postcss autoprefixer

# Initialize Tailwind config
npx tailwindcss init -p`} />
                </div>

                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">4. Configure Tailwind CSS</h4>
                    <p className="text-text-secondary dark:text-dark-text-secondary mb-3">
                        Update your <code>tailwind.config.js</code> to include the design system's content paths:
                    </p>
                    <CodeBlock code={`/** @type {import('tailwindcss').Config} */
export default {
  content: [
    "./index.html",
    "./src/**/*.{js,ts,jsx,tsx}",
    "./node_modules/@acutrack-bookprint/acutrack-ds/**/*.{js,ts,jsx,tsx}"
  ],
  theme: {
    extend: {},
  },
  plugins: [],
}`} />
                </div>

                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">5. Import CSS and Components</h4>
                    <p className="text-text-secondary dark:text-dark-text-secondary mb-3">
                        Import the design system's CSS and start using components:
                    </p>
                    <CodeBlock code={`// In your main CSS file (e.g., index.css or App.css)
@import '@acutrack-bookprint/acutrack-ds/dist/style.css';

// In your React component
import React from 'react';
import { Button, Card, Icon } from '@acutrack-bookprint/acutrack-ds';

function App() {
  return (
    <div className="p-6">
      <Card>
        <Card.Body>
          <h1 className="text-2xl font-bold mb-4">Welcome to Acutrack</h1>
          <Button variant="primary" leftIcon={<Icon>rocket_launch</Icon>}>
            Get Started
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
}

export default App;`} />
                </div>

                <div className="p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary mb-3">6. Package Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="font-semibold text-text-primary dark:text-dark-text-primary">Package Name:</p>
                            <p className="text-text-secondary dark:text-dark-text-secondary">@acutrack-bookprint/acutrack-ds</p>
                        </div>
                        <div>
                            <p className="font-semibold text-text-primary dark:text-dark-text-primary">Current Version:</p>
                            <p className="text-text-secondary dark:text-dark-text-secondary">1.0.23</p>
                        </div>
                        <div>
                            <p className="font-semibold text-text-primary dark:text-dark-text-primary">License:</p>
                            <p className="text-text-secondary dark:text-dark-text-secondary">MIT</p>
                        </div>
                        <div>
                            <p className="font-semibold text-text-primary dark:text-dark-text-primary">Repository:</p>
                            <p className="text-text-secondary dark:text-dark-text-secondary">GitHub</p>
                        </div>
                    </div>
                </div>
            </div>
        </SubSection>

        <SubSection title="The Developer Workflow">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                Using the design system is a straightforward process. Here’s how to go from idea to implementation:
            </p>
            <div className="space-y-8 not-prose">
                <div className="flex items-start gap-6 p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <span className="text-4xl font-heading text-brand-orange">1</span>
                    <div>
                        <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary">Find a Component</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary">Use the sidebar navigation to find the component you need. For example, to find the button component, navigate to <strong>Forms → Buttons</strong>.</p>
                    </div>
                </div>
                <div className="flex items-start gap-6 p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <span className="text-4xl font-heading text-brand-orange">2</span>
                    <div>
                        <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary">Use the Interactive Workbench</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary">Most component pages feature an "Interactive Workbench." Use the controls to configure the component's props and see a live preview update in real-time.</p>
                    </div>
                </div>
                <div className="flex items-start gap-6 p-6 rounded-lg bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color">
                    <span className="text-4xl font-heading text-brand-orange">3</span>
                    <div>
                        <h4 className="font-bold text-lg text-text-primary dark:text-dark-text-primary">Copy the Code Snippet</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary">As you configure the component, the code snippet below it updates automatically. This generated snippet shows the exact JSX needed to render the component with your selected props.</p>
                    </div>
                </div>
            </div>
        </SubSection>

        <SubSection title="Technology Stack">
            <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary dark:text-dark-text-secondary">
                <p>The system is built on a modern, robust technology stack chosen for its scalability and developer experience:</p>
                <ul>
                    <li><strong>React & TypeScript:</strong> All components are built as React functional components with strict TypeScript typing. This ensures type safety and provides excellent autocompletion in your IDE.</li>
                    <li><strong>Tailwind CSS:</strong> Styling is handled exclusively through Tailwind's utility classes. There are no separate CSS files to manage. This utility-first approach allows for rapid prototyping and ensures all styling adheres to the system's design tokens.</li>
                </ul>
            </div>
        </SubSection>
        
        <SubSection title="Core Philosophy: Styling with Design Tokens">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                The most important principle to understand is that all styling is based on a central set of **design tokens**. These tokens (for colors, spacing, fonts, etc.) are defined in the <code>tailwind.config</code> and are applied using utility classes.
            </p>
            <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary dark:text-dark-text-secondary">
               <p><strong>You should never use hard-coded values.</strong> Instead of writing <code>color: '#1B3A7B'</code>, you use the utility class <code>text-brand-navy</code>. This ensures that every visual element is consistent and that the entire application's theme can be updated from a single source of truth.</p>
            </div>
            <CodeBlock code={stylingCode} />
        </SubSection>

        <SubSection title="Component Usage">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                All components are exported from the design system package. You can import and use them in your own components as shown below. Most components accept props for customization, which are documented in their respective sections.
            </p>
            <CodeBlock code={componentUsageCode} />
        </SubSection>

        <SubSection title="State Management Philosophy">
             <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                Our components are designed to be "controlled" components. This means they are stateless and their behavior is controlled by props passed from a parent component. For example, to open a <code>{'<Modal>'}</code>, you pass it an <code>isOpen={true}</code> prop. The design system does not manage its own state; your application does. This makes our components highly flexible and easy to integrate into any state management solution (e.g., React's `useState`, Redux, etc.).
            </p>
        </SubSection>
    </SectionContainer>
);
